import type { FC } from "react";
import { Icon, ospThemes } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import {
  getAttachmentDisplayName,
  getSenderAttachmentChipErrorVisual,
  getSenderAttachmentChipVisual,
} from "./senderAttachmentChipStyle";
import type { SenderAttachment } from "./type";

const OR_SCENE = "Chat";

export interface SenderAttachmentChipProps {
  attachment: SenderAttachment;
  index: number;
  onRemove: (index: number) => void;
  /** Filename + type label column className */
  nameClassName?: string;
}

export const SenderAttachmentChip: FC<SenderAttachmentChipProps> = ({
  attachment,
  index,
  onRemove,
  nameClassName,
}) => {
  const isUploading = attachment.status === "uploading";
  const isError = attachment.status === "error";
  const visual = isError
    ? getSenderAttachmentChipErrorVisual(attachment.filename)
    : getSenderAttachmentChipVisual(attachment.filename);
  const displayName = getAttachmentDisplayName(attachment.filename);

  return (
    <div
      className={cn(
        "group flex shrink-0 cursor-pointer items-center gap-1 rounded-3 p-1.5",
        isError
          ? "bg-background_accent2_overlay10"
          : "bg-background_white_overlay5",
      )}
      data-loading={isUploading}
    >
      <div className="flex min-w-0 items-center gap-2">
        <div
          className={cn(
            "relative flex size-8 shrink-0 items-center justify-center rounded-1.5",
            isUploading
              ? "bg-background_solid_neutral_tertiary"
              : visual.iconBoxClassName,
          )}
        >
          {isUploading ? (
            <Icon
              name="ic_loading2"
              size={24}
              className="animate-spin"
              color={ospThemes.colors.content_accent5_secondary}
            />
          ) : (
            <Icon
              name={visual.iconName}
              size={isError ? 16 : 20}
              color={ospThemes.colors.content_inverse_primary}
            />
          )}
        </div>
        <div
          className={cn("flex min-w-0 flex-col gap-1 w-[100px]", nameClassName)}
        >
          <span className="body_s truncate text-content_secondary">
            {displayName}
          </span>
          <span className="body_xs truncate text-content_tertiary">
            {visual.typeLabel}
          </span>
        </div>
      </div>
      <Icon
        data-openreplay-label={buildOpenReplayLabel(
          OR_SCENE,
          "Attachment",
          "Remove",
          attachment.filename,
        )}
        name="close"
        size={16}
        className="shrink-0 text-content_primary bg-background_white_overlay10 rounded-full p-0.5"
        onClick={(event) => {
          event.stopPropagation();
          onRemove(index);
        }}
      />
    </div>
  );
};
